// eslint-disable-next-line ts/ban-ts-comment
// @ts-ignore cache issues
import type { Sandbox } from "polystore-cache"

import * as Comlink from "comlink"

import { mimeTypeString } from "~/classes/entity"
import SandboxWorker from "~/workers/sandbox.worker?worker"

interface State {
  isSandboxWorkerSetUp: boolean
}

let sandboxState: Ref<State>
const sandboxWorkers: Comlink.Remote<Sandbox>[] = []

function defaultState(): State {
  return {
    isSandboxWorkerSetUp: false,
  }
}
function clearSandboxState() {
  sandboxState.value = defaultState()
}

const _console = useLogger("use-sandbox", theme.colors.purple.hex)

function sandboxWorkerConcurrency() {
  return clamp(navigator.hardwareConcurrency, 4, 16)
}

function sandboxWorkerCount() {
  return len(sandboxWorkers)
}

async function initSandboxWorker() {
  if (sandboxState.value.isSandboxWorkerSetUp)
    return
  sandboxState.value.isSandboxWorkerSetUp = true
  if (isDesktop())
    return
  if (isTesting())
    return

  // the first sandbox worker we set up synchronously, then we set up the
  // rest asynchronously.
  const tasks = range(sandboxWorkerConcurrency()).map(async (i) => {
    const worker = new SandboxWorker({ name: `Sandbox Worker ${i}` })
    await waitForFirstWorkerMessage(worker)
    sandboxWorkers.push(Comlink.wrap(worker))
  })

  // awaits the first worker to be set up, and let the rest set up in the
  // background
  await Promise.race(tasks)
}

function hasInitializedWorkers() {
  return sandboxWorkers.length > 0
}

/** Returns one of our sandbox workers at random */
function sandboxWorker() {
  if (isTesting()) {
    const mocks = useMocks()
    return mocks.sandbox!
  }
  if (!hasInitializedWorkers())
    throw new Error("Sandbox worker uninitialized")
  return sandboxWorkers[roll(sandboxWorkerCount())]
}

async function getOfflineFileThumbnails(file: StaticFile): Promise<string[]> {
  const set = file.thumbnailSet()
  if (!set)
    return []
  const possible = Object.values(set.images).map(i => i.uri)
  if (isDesktop()) {
    const { getThumbnailOfflineStatus } = useTauri()
    return await getThumbnailOfflineStatus(possible)
  }
  else {
    const thumbnails = await sandboxWorker().getThumbnailOfflineStatus(possible)
    return thumbnails
  }
}

async function loadThumbnailBytesFromSandbox(
  thumbnailUri: string,
): Promise<ArrayBuffer> {
  if (isDesktop()) {
    const { loadThumbnail } = useTauri()
    return await loadThumbnail(thumbnailUri)
  }
  else {
    return await sandboxWorker().loadThumbnailBytesFromSandbox(thumbnailUri)
  }
}

async function saveThumbnailToSandbox(
  thumbnailUri: string,
  buffer: ArrayBuffer,
) {
  if (isDesktop()) {
    const { saveThumbnail } = useTauri()
    const bytes = new Uint8Array(buffer)
    return await saveThumbnail(bytes, thumbnailUri)
  }
  else if (isTesting()) {
    return await sandboxWorker().saveThumbnail(thumbnailUri, buffer)
  }
  else {
    return await sandboxWorker().saveThumbnail(thumbnailUri, Comlink.transfer(buffer, [buffer]))
  }
}

async function downloadFileToSandbox(
  checksum: Uint8Array,
  presignedUrl: string,
) {
  if (isDesktop()) {
    const { downloadFileToSandbox } = useTauri()
    return await downloadFileToSandbox(checksum, presignedUrl)
  }

  return await sandboxWorker().downloadFileToSandbox(checksum, presignedUrl)
}

async function getCachedFileFromSandboxPath(sandboxPath: string, mimeType?: EntityMimeType) {
  const slug = sandboxPath.split("/").pop()
  const root = await navigator.storage.getDirectory()
  const handle = await root.getFileHandle(slug!)
  const file = await handle.getFile()

  if (!mimeType)
    return file

  // because files / blobs are immutable, we need to create a new file if we want to change
  // the mime type of this path.
  const type = mimeTypeString(mimeType)
  return new File([file], file.name, { type })
}

/** TODO: this returns either string, null, or undefined. Remove the null? */
async function getFileOfflineStatus(file: StaticFile): Promise<string | undefined> {
  const checksum = file.checksum()
  if (!isDefined(checksum)) {
    // console.error("File has no checksum, cannot load from sandbox", checksum)
    return undefined
  }
  if (isDesktop()) {
    const { getFileOfflineStatus } = useTauri()
    return await getFileOfflineStatus(checksum) ?? undefined
  }
  else {
    return await sandboxWorker().getFileOfflineStatus(checksum)
  }
}

async function loadCachedImageAsTranscodedPngUrl(file: StaticFile) {
  const sandboxPath = await getFileOfflineStatus(file)
  if (!sandboxPath)
    throw new Error("File is not saved offline, cannot use this method")
  if (isDesktop()) {
    const { loadFileAsTranscodedPng } = useTauri()
    const array = await loadFileAsTranscodedPng(sandboxPath)
    const blob = new Blob([array.buffer])
    return URL.createObjectURL(blob)
  }
  else {
    const buffer = await sandboxWorker().loadCachedFileAsPngImageSync(sandboxPath)
    const blob = new Blob([buffer])
    return URL.createObjectURL(blob)
  }
}

async function sandboxPathAsWebFriendlyUri(sandboxPath: string, mimeType?: EntityMimeType) {
  const { getSandboxPathWebFriendly } = useTauri()
  if (isDesktop())
    return await getSandboxPathWebFriendly(sandboxPath)
  const file = await getCachedFileFromSandboxPath(sandboxPath, mimeType)
  const blobUri = URL.createObjectURL(file)
  return blobUri
}

export default function () {
  sandboxState = useState("sandbox-state", defaultState)

  return {
    sandboxState,
    clearSandboxState,
    initSandboxWorker,
    sandboxWorker,
    getOfflineFileThumbnails,
    saveThumbnailToSandbox,
    loadThumbnailBytesFromSandbox,
    getCachedFileFromSandboxPath,
    loadCachedImageAsTranscodedPngUrl,
    sandboxPathAsWebFriendlyUri,
    getFileOfflineStatus,
    downloadFileToSandbox,
  }
}
